import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Minus, Plus, Heart, ShoppingCart, Truck, Shield, Leaf, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { ProductCard } from '../components/ProductCard';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ProductDetailPage() {
  const { productId } = useParams();
  const { user, token } = useAuth();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/products/${productId}`);
      setProduct(response.data);

      // Fetch related products
      const relatedRes = await axios.get(`${API_URL}/products`, {
        params: { category_id: response.data.category_id, limit: 4 }
      });
      setRelatedProducts(relatedRes.data.filter(p => p.id !== productId));
    } catch (error) {
      console.error('Failed to fetch product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }

    setAddingToCart(true);
    try {
      await addToCart(product.id, selectedVariant, quantity);
      toast.success(`${product.name} added to cart`);
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleWishlist = async () => {
    if (!user || !token) {
      toast.error('Please login to add to wishlist');
      return;
    }

    try {
      if (isWishlisted) {
        await axios.delete(`${API_URL}/wishlist/${product.id}`);
        setIsWishlisted(false);
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API_URL}/wishlist/${product.id}`);
        setIsWishlisted(true);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent loading-spinner" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-lg text-muted-foreground mb-4">Product not found</p>
        <Link to="/products">
          <Button>Browse Products</Button>
        </Link>
      </div>
    );
  }

  const currentVariant = product.variants[selectedVariant];

  return (
    <div className="min-h-screen bg-background">
      {/* Breadcrumb */}
      <div className="bg-muted/30 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-muted-foreground hover:text-primary">Home</Link>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <Link to="/products" className="text-muted-foreground hover:text-primary">Products</Link>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <span className="text-stone-900 font-medium truncate">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Images */}
          <div className="space-y-4">
            <div className="aspect-square rounded-2xl overflow-hidden bg-stone-50">
              <img
                src={product.images?.[selectedImage] || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800'}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-3">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImage === idx ? 'border-primary' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            {/* Badges */}
            <div className="flex gap-2 mb-4">
              {product.is_organic && (
                <span className="badge-organic">Organic</span>
              )}
              {product.is_featured && (
                <span className="badge-featured">Featured</span>
              )}
            </div>

            <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-stone-900">
              {product.name}
            </h1>

            {/* Certifications */}
            {product.certifications?.length > 0 && (
              <div className="flex gap-2 mt-3">
                {product.certifications.map((cert, idx) => (
                  <span key={idx} className="text-sm text-muted-foreground bg-stone-100 px-3 py-1 rounded-full">
                    {cert}
                  </span>
                ))}
              </div>
            )}

            {/* Price */}
            <div className="mt-6">
              <span className="text-3xl font-semibold text-primary">
                ₹{currentVariant.price}
              </span>
              <span className="text-muted-foreground ml-2">/ {currentVariant.size}</span>
            </div>

            {/* Description */}
            <p className="mt-6 text-stone-600 leading-relaxed">
              {product.description}
            </p>

            {/* Variant Selection */}
            <div className="mt-8">
              <h3 className="font-medium text-stone-900 mb-3">Select Size</h3>
              <div className="flex flex-wrap gap-3">
                {product.variants.map((variant, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedVariant(idx)}
                    className={`px-6 py-3 rounded-full border-2 font-medium transition-all ${
                      selectedVariant === idx
                        ? 'border-primary bg-primary text-white'
                        : 'border-stone-200 hover:border-primary text-stone-700'
                    }`}
                    data-testid={`variant-btn-${idx}`}
                  >
                    {variant.size} - ₹{variant.price}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mt-8">
              <h3 className="font-medium text-stone-900 mb-3">Quantity</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-stone-200 rounded-full">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="rounded-full"
                    data-testid="decrease-quantity"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-12 text-center font-medium">{quantity}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(quantity + 1)}
                    className="rounded-full"
                    data-testid="increase-quantity"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <span className="text-sm text-muted-foreground">
                  {currentVariant.stock > 0 ? `${currentVariant.stock} in stock` : 'Out of stock'}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-8 flex gap-4">
              <Button
                onClick={handleAddToCart}
                disabled={addingToCart || currentVariant.stock === 0}
                className="flex-1 bg-primary hover:bg-primary/90 text-white rounded-full py-6 text-lg"
                data-testid="add-to-cart"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                {addingToCart ? 'Adding...' : 'Add to Cart'}
              </Button>
              <Button
                variant="outline"
                onClick={handleWishlist}
                className="rounded-full px-6 py-6 border-2"
                data-testid="add-to-wishlist"
              >
                <Heart className={`h-5 w-5 ${isWishlisted ? 'fill-red-500 text-red-500' : ''}`} />
              </Button>
            </div>

            {/* Features */}
            <div className="mt-10 grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-muted/50 rounded-xl">
                <Truck className="h-6 w-6 mx-auto text-primary mb-2" />
                <p className="text-sm font-medium">Free Delivery</p>
                <p className="text-xs text-muted-foreground">Above ₹500</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-xl">
                <Shield className="h-6 w-6 mx-auto text-primary mb-2" />
                <p className="text-sm font-medium">Certified</p>
                <p className="text-xs text-muted-foreground">FSSAI Approved</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-xl">
                <Leaf className="h-6 w-6 mx-auto text-primary mb-2" />
                <p className="text-sm font-medium">100% Organic</p>
                <p className="text-xs text-muted-foreground">No Chemicals</p>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-20">
            <h2 className="font-serif text-2xl font-semibold text-stone-900 mb-8">
              Related Products
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.slice(0, 4).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
